import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { twofaAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { ShieldCheck, QrCode, Copy, CheckCircle, WarningCircle, Key } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function TwoFAPage() {
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('idle');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try {
      const { data } = await twofaAPI.status();
      setStatus(data);
    } catch (err) {
      console.error('Failed to load 2FA status:', err);
      toast.error('Failed to load 2FA status');
    }
  };

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await twofaAPI.setup();
      setSetupData(data);
      setStep('scan');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const confirmEnable = async () => {
    if (!verifyCode || verifyCode.length < 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const { data } = await twofaAPI.enable(verifyCode);
      setBackupCodes(data.backup_codes);
      setStep('backup');
      toast.success('2FA enabled!');
      loadStatus();
      refreshUser();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const handleDisable = async () => {
    if (!disableCode) { toast.error('Enter your 2FA code'); return; }
    setLoading(true);
    try {
      await twofaAPI.disable(disableCode);
      toast.success('2FA disabled');
      setStatus({ enabled: false });
      setStep('idle');
      setDisableCode('');
      refreshUser();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const copyBackupCodes = () => {
    if (backupCodes) {
      navigator.clipboard.writeText(backupCodes.join('\n'));
      toast.success('Backup codes copied');
    }
  };

  const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
  const token = localStorage.getItem('divispend_access_token');

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-[#F5F5F5]'}`}>
      <header className={`sticky top-0 z-20 border-b ${isDark ? 'bg-[#0A0A0A]/90 border-[#222222]' : 'bg-white/90 border-[#E5E5E5]'} backdrop-blur-md`}>
        <div className="max-w-xl mx-auto px-4 h-14 flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-[#22C55E]" weight="fill" />
          <h1 className={`font-bold ${isDark ? 'text-white' : 'text-[#111111]'}`} data-testid="2fa-title">Two-Factor Authentication</h1>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6 space-y-6">
        {/* Status Card */}
        <Card className={isDark ? 'bg-[#111111] border-[#222222]' : 'bg-white border-[#E5E5E5]'}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {status?.enabled ? (
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" weight="fill" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <WarningCircle className="w-5 h-5 text-yellow-400" weight="fill" />
                  </div>
                )}
                <div>
                  <p className={`font-bold ${isDark ? 'text-white' : 'text-[#111111]'}`}>
                    {status?.enabled ? '2FA Enabled' : '2FA Not Enabled'}
                  </p>
                  <p className="text-xs text-[#999999]">
                    {status?.enabled ? `Since ${new Date(status.enabled_at).toLocaleDateString()}` : 'Protect your account with an authenticator app'}
                  </p>
                </div>
              </div>
              {!status?.enabled && step === 'idle' && (
                <Button onClick={startSetup} disabled={loading} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-bold" data-testid="setup-2fa-btn">
                  {loading ? 'Setting up...' : 'Enable 2FA'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Setup: Scan QR */}
        {step === 'scan' && setupData && (
          <Card className={isDark ? 'bg-[#111111] border-[#222222]' : 'bg-white border-[#E5E5E5]'}>
            <CardContent className="p-5 space-y-4">
              <h2 className={`font-bold ${isDark ? 'text-white' : 'text-[#111111]'}`}>Step 1: Scan QR Code</h2>
              <p className="text-sm text-[#999999]">Scan this QR code with Google Authenticator, Authy, or any TOTP app.</p>

              <div className="flex justify-center py-4">
                <div className="bg-white p-3 rounded-lg">
                  <img src={`${API}/2fa/setup/qr?token=${token}`} alt="2FA QR" className="w-48 h-48" data-testid="2fa-qr-image" />
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-[#999999]">Or enter this key manually:</p>
                <div className="flex gap-2">
                  <code className={`flex-1 text-sm p-2 rounded font-mono ${isDark ? 'bg-[#0A0A0A] text-[#22C55E]' : 'bg-[#F5F5F5] text-[#111111]'}`} data-testid="manual-key">
                    {setupData.manual_entry_key}
                  </code>
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(setupData.manual_entry_key); toast.success('Key copied'); }} className="border-[#333333]">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Step 2: Enter the 6-digit code from your app</Label>
                <div className="flex gap-2">
                  <Input
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className={`text-center text-2xl tracking-widest font-mono flex-1 ${isDark ? 'bg-[#0A0A0A] border-[#333333] text-white' : ''}`}
                    maxLength={6}
                    data-testid="2fa-verify-input"
                  />
                  <Button onClick={confirmEnable} disabled={loading || verifyCode.length < 6} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-bold px-6" data-testid="2fa-confirm-btn">
                    {loading ? '...' : 'Verify'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Backup Codes */}
        {step === 'backup' && backupCodes && (
          <Card className="bg-[#111111] border-yellow-500/30">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-yellow-400" weight="fill" />
                <h2 className="text-white font-bold">Save Your Backup Codes</h2>
              </div>
              <p className="text-sm text-yellow-400/80">Store these codes safely. Each can be used once if you lose access to your authenticator app.</p>

              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code) => (
                  <code key={code} className="bg-[#0A0A0A] text-[#22C55E] p-2 rounded text-center font-mono text-sm" data-testid={`backup-code-${code}`}>{code}</code>
                ))}
              </div>

              <div className="flex gap-2">
                <Button onClick={copyBackupCodes} variant="outline" className="flex-1 border-[#333333] text-white" data-testid="copy-backup-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy All
                </Button>
                <Button onClick={() => { setStep('idle'); setBackupCodes(null); }} className="flex-1 bg-[#22C55E] hover:bg-[#1EA34B] text-black font-bold" data-testid="done-btn">
                  I've Saved Them
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Disable 2FA */}
        {status?.enabled && step === 'idle' && (
          <Card className={isDark ? 'bg-[#111111] border-[#222222]' : 'bg-white border-[#E5E5E5]'}>
            <CardContent className="p-5 space-y-3">
              <h2 className={`font-bold text-sm ${isDark ? 'text-white' : 'text-[#111111]'}`}>Disable 2FA</h2>
              <p className="text-xs text-[#999999]">Enter your current 2FA code to disable two-factor authentication.</p>
              <div className="flex gap-2">
                <Input
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className={`text-center font-mono flex-1 ${isDark ? 'bg-[#0A0A0A] border-[#333333] text-white' : ''}`}
                  maxLength={6}
                  data-testid="disable-2fa-input"
                />
                <Button onClick={handleDisable} disabled={loading} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid="disable-2fa-btn">
                  Disable
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
